import React, { SyntheticEvent, useContext, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItem,
  Snackbar,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import { ChargifyState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_provider_pb";
import { Tier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/common_pb";
import { PaymentCollectionMethod } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/payment_method_pb";
import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { PaymentMethod } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";

import SubscriptionAction from "./subscription-action";
import {
  updateCancelAtEndOfPeriod,
  updatePaymentMethod,
} from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { FEATURE_FLAG_CREDIT_CARD_LESS_EXPERIENCE } from "constants/billing-experiences";
import {
  DEFAULT_SNACKBAR_DURATION,
  EventTypeReason,
  NO_DISPLAY_DATA,
} from "constants/index";
import { UseGetFeatureFlagValue } from "context/feature-flags-context";
import { OrgDataContext, SubDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { UrlParamTypes } from "types/org-display";

interface UpdatePaymentCollectionMethodZuoraProps {
  refreshOrgData?: () => void;
}

const UpdatePaymentCollectionMethodZuora = ({
  refreshOrgData,
}: UpdatePaymentCollectionMethodZuoraProps) => {
  const theme = useTheme();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  // Create display mapping for PaymentCollectionMethod
  const paymentCollectionMethodDisplayMapping = {
    [PaymentCollectionMethod.AUTOMATIC]: "Automatic",
    [PaymentCollectionMethod.INVOICE]: "Invoice",
    [PaymentCollectionMethod.UNSPECIFIED]: "Unspecified",
  };

  const { orgData } = useContext(OrgDataContext);
  const { subData } = useContext(SubDataContext);
  const { showError } = useContext(SnackbarContext);
  const hasCCLExp = UseGetFeatureFlagValue(
    FEATURE_FLAG_CREDIT_CARD_LESS_EXPERIENCE
  );

  const normalisedSubData = subData?.subscription;

  // Use normalized subscription data for payment method
  const currentPaymentMethod = normalisedSubData?.paymentCollectionMethod;

  const [targetPaymentMethod, setTargetPaymentMethod] = useState(
    currentPaymentMethod === PaymentCollectionMethod.AUTOMATIC
      ? PaymentCollectionMethod.INVOICE
      : PaymentCollectionMethod.AUTOMATIC
  );

  const isFreeOrg = normalisedSubData?.tier === Tier.FREE;

  const hasPaymentMethod =
    currentPaymentMethod !== undefined &&
    currentPaymentMethod !== PaymentCollectionMethod.UNSPECIFIED;

  const isButtonDisabled = !hasPaymentMethod || isFreeOrg;

  // Check for credit card - parse JSON to get Zuora billing details
  let haveCreditCard = false;
  if (subData?.json) {
    try {
      const parsedJson = JSON.parse(subData.json);
      haveCreditCard = parsedJson?.zuora?.billing?.maskedCardNumber !== "";
    } catch {
      // Fallback if JSON parsing fails
      haveCreditCard = false;
    }
  }

  const isTrialing = normalisedSubData?.state === SubscriptionState.TRIALING;

  const handleUpdatePaymentMethod = () => {
    // Convert PaymentCollectionMethod to PaymentMethod for the API
    const apiPaymentMethod =
      targetPaymentMethod === PaymentCollectionMethod.AUTOMATIC
        ? PaymentMethod.AUTOMATIC
        : PaymentMethod.INVOICE;

    // Convert Zuora subscription state to equivalent Chargify state
    let chargifyState = ChargifyState.UNSPECIFIED;
    switch (normalisedSubData?.state) {
      case SubscriptionState.ACTIVE:
        chargifyState = ChargifyState.ACTIVE;
        break;
      case SubscriptionState.TRIALING:
        chargifyState = ChargifyState.TRIALING;
        break;
      case SubscriptionState.PAST_DUE:
        chargifyState = ChargifyState.PAST_DUE;
        break;
      case SubscriptionState.CANCELED:
        chargifyState = ChargifyState.CANCELED;
        break;
      default:
        chargifyState = ChargifyState.UNSPECIFIED;
    }

    return updatePaymentMethod(
      {
        subscriptionId: normalisedSubData?.subscriptionId || "",
        paymentMethod: apiPaymentMethod,
        organizationId: orgData.id,
        chargifyState,
      },
      callOptionsHeaders(regionCode, orgData.id)
    )
      .then(() => {
        if (refreshOrgData) {
          refreshOrgData();
        }
        const newTarget =
          targetPaymentMethod === PaymentCollectionMethod.AUTOMATIC
            ? PaymentCollectionMethod.INVOICE
            : PaymentCollectionMethod.AUTOMATIC;
        setTargetPaymentMethod(newTarget);
      })
      .catch(showError);
  };

  const handleCancelAtEndOfPeriod = () => {
    const cancelAtEndOfPeriod =
      targetPaymentMethod === PaymentCollectionMethod.AUTOMATIC;
    const message = cancelAtEndOfPeriod ? "Credit card less trial" : "";

    return updateCancelAtEndOfPeriod(
      {
        organizationId: toUUID(roleId),
        subscriptionId: normalisedSubData?.subscriptionId || "",
        cancelAtEndOfPeriod,
        message,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        if (refreshOrgData) {
          refreshOrgData();
        }
      })
      .catch(showError);
  };

  const resetState = () => {
    // No tax exemption state to reset for Zuora
  };

  const handleSubmit = async () => {
    setShowLoadingSpinner(true);
    await handleUpdatePaymentMethod();
    if (hasCCLExp && isTrialing && !haveCreditCard) {
      await handleCancelAtEndOfPeriod();
    }
    setOpenSuccessAlert(true);
    setShowLoadingSpinner(false);
  };

  const handleClose = (
    _event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const warnContainerSx = {
    background: "#ffd4d4",
    border: "1px solid #941c1c",
    color: "#941c1c",
    borderRadius: 1,
    padding: theme.spacing(1.25),
    margin: "6px 4px 0px",
  };

  const displayCancelTrialEndOfPeriodMessage = () => {
    const cancelAtEndOfPeriodStatus =
      targetPaymentMethod === PaymentCollectionMethod.AUTOMATIC
        ? "set"
        : "remove";
    const paymentMethodMessage =
      paymentCollectionMethodDisplayMapping[targetPaymentMethod].toUpperCase();

    return (
      <Grid item xs={12} data-testid={"cancel-at-end-of-period-status-message"}>
        <Box sx={warnContainerSx}>
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            This organization is on the credit card less (CCL) trial.
          </ListItem>
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            There is no payment method attached.
          </ListItem>
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            Changing to <b>{paymentMethodMessage}</b> will{" "}
            <b>{cancelAtEndOfPeriodStatus} </b> the cancel at end of period
            status.
          </ListItem>
        </Box>
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <SubscriptionAction
        title="Payment Collection Method:"
        displayInfo={
          currentPaymentMethod !== undefined &&
          currentPaymentMethod !== PaymentCollectionMethod.UNSPECIFIED
            ? paymentCollectionMethodDisplayMapping[currentPaymentMethod]
            : NO_DISPLAY_DATA
        }
      >
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            disabled={isButtonDisabled}
            fullWidth
            onClick={() => setOpenConfirmDialog(true)}
            data-testid="change-payment-method-btn"
          >
            CHANGE TO{" "}
            {paymentCollectionMethodDisplayMapping[targetPaymentMethod]}
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Change Payment Collection Method"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        width="md"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => {
          handleSubmit();
        }}
        onCancel={resetState}
      >
        <Grid container spacing={1}>
          {hasCCLExp && isTrialing && !haveCreditCard
            ? displayCancelTrialEndOfPeriodMessage()
            : null}

          <Grid item xs={12}>
            <Typography>
              Are you sure you want to change the payment collection method to{" "}
              {""}
              <b>
                {paymentCollectionMethodDisplayMapping[
                  targetPaymentMethod
                ].toUpperCase()}
              </b>
              ?
            </Typography>
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleClose}
      >
        <Alert severity="success" variant="filled">
          Payment collection method updated
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default UpdatePaymentCollectionMethodZuora;
